<template>
  <div
    class="bg-primary-700 flex"
    :class="{
      'min-h-[calc(100%-40px)] mt-10': getShowPastDueNotification,
      'min-h-full': !getShowPastDueNotification,
    }"
  >
    <div
      :class="
        cn(
          'bg-black/25 absolute top-0 left-0 bottom-0 right-0 opacity-0 z-[-1]',
          sidebarOpen && 'opacity-100 z-[1]'
        )
      "
      @click="sidebarOpen = !sidebarOpen"
    />
    <aside
      :class="
        cn(
          'bg-[#252929] w-[280px] transition-transform duration-200 ease-in-out',
          'absolute top-0 left-0 md:relative h-full z-10 transform -translate-x-full md:translate-x-0',
          'flex flex-col justify-between',
          sidebarOpen && 'translate-x-0'
        )
      "
    >
      <div>
        <workspace-switcher />
        <Tabs v-model="selectedTab" orientation="vertical" class="mt-4">
          <TabsList>
            <TabsIndicator variant="highlight" />
            <Protect
              v-for="item in navigationItemsList"
              :key="item.name"
              :permissions="item?.permissions"
              :skip-protect="item?.skipProtect"
            >
              <TabsTrigger :value="item.route">
                <RouterLink
                  :to="{ name: item.route }"
                  class="flex items-center gap-2 p-3 text-neutral-500 hover:text-neutral-100"
                >
                  <component :is="item.icon" class="h-4 w-4" /> {{ item.name }}
                </RouterLink>
              </TabsTrigger>
            </Protect>
          </TabsList>
        </Tabs>
      </div>
      <FreeWorkspace v-if="currentSubscriptionPlan.name === 'Free'" />
    </aside>
    <main class="flex-1 p-6 md:px-7 md:py-7 overflow-y-auto relative">
      <div class="absolute top-6 left-6 md:hidden">
        <IconButton
          type="button"
          variant="ghost"
          class="border-transparent text-neutral-500"
          @click="sidebarOpen = !sidebarOpen"
        >
          <AlignJustify :size="20" />
        </IconButton>
      </div>
      <div class="absolute top-8 right-8">
        <ProfileMenu avatar-class="size-8" />
      </div>
      <router-view :key="$route.path" />
    </main>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import {
  AlignJustify,
  Folders,
  Users,
  UserCog,
  Newspaper,
  Settings,
  Code2,
} from 'lucide-vue-next';
import { useStore } from 'vuex';
import ProfileMenu from '@components/board/ProfileMenu.vue';
import Protect from '@/hoc/Protect.vue';
import { SYS_PERMISSIONS } from '@/constants.js';
import { IconButton } from '@ui/components/icon-button';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsIndicator,
} from '@ui/components/tabs';

import { useRoute } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import { queries } from '@/queries/index.js';
import { cn } from '../../ui/utils.js';
import WorkspaceSwitcher from './components/WorkspaceSwitcher.vue';
import FreeWorkspace from './components/FreeWorkspace.vue';

const route = useRoute();

const selectedTab = computed({
  get() {
    return route.matched[1].name;
  },
  set() {},
});

const store = useStore();

const getShowPastDueNotification = computed(() => {
  return store.getters['workspace/getShowPastDueNotification'];
});

const sidebarOpen = ref(false);

const isInCurrentWorkspace = computed(
  () => store.getters['user/externalMemberships/isInCurrentWorkspace']
);

const currentSubscriptionPlan = computed(
  () =>
    store.getters['workspace/getCurrentSubscriptionPlan'] ?? { name: 'Free' }
);

const workspaceId = computed(() => store.getters['workspace/getId']);

useQuery(queries.permissions.workspace(workspaceId));
useQuery(queries.features.workspace(workspaceId));

const navigationItems = [
  {
    name: 'Projects',
    route: 'workspace-projects',
    permissions: [SYS_PERMISSIONS.SYS_PROJECTS_READ],
    skipProtect: isInCurrentWorkspace,
    icon: Folders,
  },
  {
    name: 'People',
    route: 'workspace-people',
    permissions: [
      SYS_PERMISSIONS.SYS_MEMBERSHIPS_READ,
      SYS_PERMISSIONS.SYS_MEMBERSHIPS_MANAGE,
    ],
    icon: Users,
  },
  {
    name: 'Roles',
    route: 'workspace-roles',
    permissions: [SYS_PERMISSIONS.SYS_ROLES_MANAGE],
    icon: UserCog,
  },
  {
    name: 'Settings',
    route: 'workspace-settings',
    permissions: [SYS_PERMISSIONS.SYS_WORKSPACE_MANAGE],
    icon: Settings,
  },
  {
    name: 'Billing',
    route: 'workspace-billing',
    permissions: [SYS_PERMISSIONS.SYS_BILLING_MANAGE],
    icon: Newspaper,
  },
  {
    name: 'API',
    route: 'workspace-api',
    permissions: [SYS_PERMISSIONS.SYS_API_READ],
    icon: Code2,
  },
];

const navigationItemsList = ref(navigationItems);
</script>
